/**
 * Data precedence for cost / fuel / hours / Mx inputs.
 *
 * Primary = most accurate reliable source available. When a better source is
 * wired (SimConnect actuals, combustion timers, user post-flight), use it
 * over older estimates. Weaker sources stay on file for comparison — they
 * are never discarded, just not preferred.
 *
 * Highest first:
 * 1. User-edited post-flight / arrival values (explicit override)
 * 2. SimConnect / live sim actuals
 * 3. SimBrief OFP planned figures
 * 4. Legacy desk estimates / ballpark / typed defaults
 */

export const DATA_SOURCE_KINDS = ["user_override", "simconnect", "simbrief_ofp", "desk_estimate"] as const;

export type DataSourceKind = (typeof DATA_SOURCE_KINDS)[number];

export const DATA_SOURCE_RANK: Record<DataSourceKind, number> = {
  user_override: 0,
  simconnect: 1,
  simbrief_ofp: 2,
  desk_estimate: 3,
};

export const DATA_SOURCE_LABEL: Record<DataSourceKind, string> = {
  user_override: "User override",
  simconnect: "SimConnect actual",
  simbrief_ofp: "SimBrief OFP planned",
  desk_estimate: "Desk estimate",
};

export type RankedValue<T> = {
  value: T;
  source: DataSourceKind;
};

export function isDataSourceKind(value: unknown): value is DataSourceKind {
  return value === "user_override" || value === "simconnect" || value === "simbrief_ofp" || value === "desk_estimate";
}

export function hydrateDataSourceKind(raw?: unknown): DataSourceKind | null {
  return isDataSourceKind(raw) ? raw : null;
}

function isPresentNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/**
 * Pick the first present number in precedence order. `null` / `undefined` /
 * NaN / negative are missing and fall through.
 */
export function pickPreferredNumber(
  candidates: ReadonlyArray<{ value?: number | null; source: DataSourceKind }>,
): RankedValue<number> | null {
  const ranked = [...candidates].sort((a, b) => DATA_SOURCE_RANK[a.source] - DATA_SOURCE_RANK[b.source]);
  for (const row of ranked) {
    if (isPresentNumber(row.value)) return { value: row.value, source: row.source };
  }
  return null;
}

/** Fuel used (any unit) — same chain as hours / cost inputs. */
export function pickPreferredFuel(input: {
  userOverride?: number | null;
  simActual?: number | null;
  ofpPlanned?: number | null;
  deskEstimate?: number | null;
}): RankedValue<number> | null {
  return pickPreferredNumber([
    { value: input.userOverride, source: "user_override" },
    { value: input.simActual, source: "simconnect" },
    { value: input.ofpPlanned, source: "simbrief_ofp" },
    { value: input.deskEstimate, source: "desk_estimate" },
  ]);
}

/** Hours (air / engine / APU / block). OFP planned time is a last-resort estimate. */
export function pickPreferredHours(input: {
  userOverride?: number | null;
  simActual?: number | null;
  ofpPlanned?: number | null;
  deskEstimate?: number | null;
}): RankedValue<number> | null {
  return pickPreferredFuel(input);
}
